import { attachProfileEmailsToApplications } from './admin-driver-search';
import { normalizeRideServices } from './driver-ride-services';

export function normalizeDriverApplication(row) {
  if (!row) return row;
  const fullName =
    String(row.full_name || row.name || '').trim() ||
    'Driver';
  const normalized = {
    ...row,
    full_name: fullName,
    status: row.status || 'pending',
    signup_source: row.signup_source || (row.app_profile_id ? 'app' : 'website'),
  };
  return {
    ...normalized,
    ride_services: normalizeRideServices(normalized),
  };
}

export async function updateDriverRideServices(supabaseClient, driverId, rideServices) {
  if (!supabaseClient || !driverId) {
    return { ok: false, error: 'Missing driver id' };
  }
  const value = String(rideServices || '').toLowerCase().trim();
  if (!['outstation', 'daily', 'both'].includes(value)) {
    return { ok: false, error: 'Invalid trip type' };
  }
  const { error } = await supabaseClient
    .from('drivers')
    .update({ ride_services: value })
    .eq('id', driverId);
  if (error) {
    const msg = error.message || 'Could not update trip type.';
    const hint =
      /ride_services|column/i.test(msg)
        ? ' Run supabase/add-driver-ride-services.sql in Supabase.'
        : '';
    return { ok: false, error: msg + hint };
  }
  return { ok: true, error: '' };
}

export function applicationSourceLabel(source) {
  return source === 'app' ? 'Saarthi app' : 'Website';
}

/** All hiring + app-synced rows for the admin review queue. */
export async function fetchDriverApplications(supabaseClient) {
  if (!supabaseClient) {
    return { applications: [], error: 'Supabase is not configured.' };
  }

  const { data, error } = await supabaseClient
    .from('drivers')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    const msg = error.message || 'Could not load applications.';
    const hint =
      error.code === '42501' || /permission|row-level/i.test(msg)
        ? ' Run supabase/admin-policies.sql and supabase/sync-app-driver-applications.sql in Supabase.'
        : '';
    return { applications: [], error: msg + hint };
  }

  let applications = (data || []).map(normalizeDriverApplication);
  applications = await attachProfileEmailsToApplications(supabaseClient, applications);

  return { applications, error: '' };
}

/** Push + email when ops approves a driver (Saarthi notify-driver-application function). */
export async function notifyDriverApplicationApproved(supabaseClient, driverId) {
  if (!supabaseClient || !driverId) return { ok: false, error: 'Missing driver id' };

  try {
    const { data, error } = await supabaseClient.functions.invoke('notify-driver-application', {
      body: { driverId: String(driverId), event: 'approved' },
    });
    if (error) {
      return { ok: false, error: error.message || String(error) };
    }
    return { ok: true, data: data || null, error: '' };
  } catch (err) {
    return { ok: false, error: err?.message || 'Notification failed' };
  }
}

/** Delete all app-origin driver applications (admin RPC or fallback). */
export async function purgeAppDriverSignups(supabaseClient) {
  if (!supabaseClient) {
    return { driversDeleted: 0, profilesCleared: 0, error: 'Supabase is not configured.' };
  }

  const { data, error } = await supabaseClient.rpc('purge_app_driver_signups');

  if (!error) {
    return {
      driversDeleted: Number(data?.drivers_deleted ?? 0),
      onlineDeleted: Number(data?.online_drivers_deleted ?? 0),
      profilesCleared: Number(data?.profiles_cleared ?? 0),
      error: '',
    };
  }

  const message = error.message || 'Purge failed';
  if (!/purge_app_driver_signups|schema cache|could not find/i.test(message)) {
    return { driversDeleted: 0, profilesCleared: 0, error: message };
  }

  return {
    driversDeleted: 0,
    profilesCleared: 0,
    error:
      'Run supabase/delete-app-driver-signups.sql in the Supabase SQL editor, then try again.',
  };
}
